/**
 * The review ask attestation (docs/50 §4.4.3, docs/52 S42), shared by every way of asking: the quick card on Home,
 * the Ask form and several at once. The wording shows in full with a tick until this person has confirmed it once
 * on this device; after that a quiet line stands in for the tick and every send still carries the attestation.
 */
import { useState } from 'preact/hooks';

/** A new version because the wording changed to email (c022): owners see and tick it again once. */
export const ATTEST_VERSION = 'rr-attest-email-2026-09';

export function attestKey(userId: string): string {
  return `ff-attest-${userId}-${ATTEST_VERSION}`;
}

export function readAttest(userId: string): boolean {
  try {
    return localStorage.getItem(attestKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function writeAttest(userId: string): void {
  try {
    localStorage.setItem(attestKey(userId), '1');
  } catch {
    /* private mode: they tick it again next time */
  }
}

export interface Attest {
  /** Confirmed before on this device: show the quiet line, not the tick. */
  remembered: boolean;
  ticked: boolean;
  setTicked: (v: boolean) => void;
  /** True when a send may go: confirmed before, or ticked now. */
  ok: boolean;
  /** Call when a send goes out with the tick on. */
  remember: () => void;
  /** What every send carries. */
  body: { accepted: true; version: string };
}

export function useAttest(userId: string): Attest {
  const [remembered, setRemembered] = useState(() => readAttest(userId));
  const [ticked, setTicked] = useState(false);
  return {
    remembered,
    ticked,
    setTicked,
    ok: remembered || ticked,
    remember: () => {
      writeAttest(userId);
      setRemembered(true);
    },
    body: { accepted: true, version: ATTEST_VERSION },
  };
}
